<template>
  <VueFinalModal
    v-model="isOpen"
    :focus-trap="false"
    :esc-to-close="!disableEsc"
    content-class="absolute inset-0"
    :content-transition="{
      enterActiveClass: 'transition ease-out duration-300 transform',
      enterFromClass: 'opacity-0',
      leaveActiveClass: 'ease-in duration-200',
      leaveToClass: 'opacity-0'
    }"
    overlay-transition="vfm-fade"
    :overlay-class="'dark:bg-black/75 backdrop-blur-sm'"
    @before-close="() => $emit('beforeClose')"
  >
    <div
      v-if="isOpen"
      class="absolute inset-0 h-full overflow-auto p-4 flex flex-wrap items-center justify-center"
      @click.self="clickOutside"
    >
      <div
        ref="content"
        method="dialog"
        :class="{ 'mx-auto relative w-full py-4 lg:py-8 px-4 sm:px-6 lg:px-12 flex flex-col gap-6 bg-neutral-50 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-300 rounded-2xl shadow-md': !boilerplate }"
        :style="!isFullscreen ? { maxWidth: typeof width === 'number' ? `${width}px` : (width || 'auto')} : {}"
        v-bind="$attrs"
      >
        <slot name="bg">
          <img
            v-if="backgroundImage"
            :src="backgroundImage"
            alt=""
            class="absolute top-0 left-0 w-full h-full object-cover rounded-2xl z-0"
            :style="`opacity: ${backgroundOpacity !== undefined ? (Number(backgroundOpacity) / 100) : 1}; object-fit: ${backgroundFit || 'cover'};`"
          />
        </slot>
        <div class="scale-100 space-y-8 w-full">
          <slot v-if="!hideHeader" name="header" :cancel="cancel" :toggleFullscreen="toggleFullscreen" :isFullscreen="isFullscreen">
            <div class="flex items-center justify-between gap-4 break-words">
              <slot name="title" :cancel="cancel">
                <h3 class="my-0 text-base sm:text-lg md:text-xl font-bold break-words" v-html="title || ''"></h3>
              </slot>

              <div class="flex items-center gap-2">
                <UiButton
                  v-if="!hideClose && allowFullscreen"
                  type="ghost"
                  class="p-1"
                  :icon="isFullscreen ? 'minimize' : 'maximize'"
                  @click="() => toggleFullscreen()"
                />
                <UiButton
                  v-if="!hideClose"
                  type="ghost"
                  icon="x"
                  iconSize="24"
                  class="px-4 h-10"
                  :text="$t('button.close')"
                  @click="() => cancel()"
                />
              </div>
            </div>
          </slot>

          <div v-if="isLoading" class="py-6 text-center w-full">
            <UiIcon class="animate-spin" icon="loader" /> {{ $t('feedback.loading') }}
          </div>
          <slot v-else />
          <slot v-if="!hideFooter" name="footer" :cancel="cancel" :confirm="confirm">
            <div class="flex flex-wrap justify-end gap-4">
              <UiButton type="primary" outline :disabled="isLoading" class="p-4 uppercase" @click="cancel">
                {{ $t('button.cancel') }}
              </UiButton>
              <UiButton type="primary" :disabled="isLoading || disabled" :loading="isLoading" class="p-4 uppercase" @click="confirm">
                {{ confirmText || $t('button.confirm') }}
              </UiButton>
            </div>
          </slot>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script lang="ts">
export interface ModalProps {
  disableEsc?: boolean
  hideClose?: boolean
  title?: string
  loading?: boolean
  width?: string | number
  confirmText?: string
  disabled?: boolean
  hideHeader?: boolean
  hideFooter?: boolean
  allowFullscreen?: boolean
  closeOnClickOutside?: boolean
  focusTrap?: boolean
  boilerplate?: boolean
  beforeOpen?: () => void | boolean | Promise<void | boolean>
  beforeCancel?: () => void | boolean | Promise<void | boolean>
  beforeConfirm?: () => void | boolean | Promise<void | boolean>
  backgroundImage?: string
  backgroundOpacity?: number
  backgroundFit?: 'cover' | 'contain' | 'auto' | 'fill' | 'none' | 'scale-down'
}
</script>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { VueFinalModal } from 'vue-final-modal'

import UiButton from '@/components/ui/button.vue'
import UiIcon from '@/components/ui/icon.vue'

defineEmits(['beforeClose'])
defineOptions({ inheritAttrs: false })

const props = defineProps<ModalProps>()

const isOpen = defineModel({ default: false })
const isLoading = computed(() => props.loading || false)
const isFullscreen = ref(false)

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value
}

let localPromise : ((value: any) => void) | null = null

const open = async (isAsync?: boolean) => {
  if (props.beforeOpen) {
    const result = await props.beforeOpen()
    if (result === false) return
  }

  isOpen.value = true

  if (isAsync) return new Promise((resolve) => {
    localPromise = resolve
  })
}

const cancel = async () => {
  if (typeof props.beforeCancel !== 'undefined' && await props.beforeCancel() === false) return
  isOpen.value = false

  if (localPromise) localPromise('cancel')
}

const confirm = async (result?: any) => {
  if (typeof props.beforeConfirm !== 'undefined' && await props.beforeConfirm() === false) return
  isOpen.value = false

  if (localPromise) localPromise(result)
}

const clickOutside = () => {
  if (props.closeOnClickOutside) {
    cancel()
  }
}

defineExpose({
  isOpen,
  open,
  cancel,
  confirm
})
</script>
